'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';

import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';

import { springBounce, staggerCard, staggerContainer } from './animations';

// 원하는 순서대로 지자체명 키워드 (institution_name에 포함되면 매칭)
const EVENT_ORDER = [
  '제주',
  '남구',
  '영암',
  '영주',
  '진천',
  '임실',
  '완도',
  '서구',
  '논산',
  '강릉',
  '통영',
  '고흥',
  '무주',
];

const getOrderIndex = (name: string | null) =>
  EVENT_ORDER.findIndex((keyword) => name?.includes(keyword));

const LOCAL_EVENT_NOTICES = [
  '본 이벤트는 각 지자체에서 주관하며 고향사랑기부제 참여 유도 및 공유 활성화를 위한 이벤트입니다.',
  '본 이벤트는 웰로의 기부자에 한해 참여 가능합니다.',
  '본 이벤트는 지역별로 1인 1회만 참여 가능하며, 동일한 지역 이벤트에 중복 참여하실 수 없습니다.',
  '본 이벤트는 내부 사정 또는 외부 제휴사의 요청으로 사전 고지 없이 변경되거나 중단될 수 있습니다.',
];

const Header = () => (
  <div className="flex flex-col items-center gap-20">
    <div className="rounded-xxlarge bg-[#f6e0cf] px-20 py-6">
      <span className="font-700 text-Gray-black typo-body1">특별기획</span>
    </div>
    <div className="flex flex-col items-center gap-8">
      <h2 className="text-center font-800 text-[#f6e0cf] typo-h4">
        고향사랑기부제
        <br />
        지역별 12월 한정 이벤트
      </h2>
      <p className="text-center font-500 text-Gray-white/70 typo-h7">
        지역마다 빵빵한 이벤트도
        <br />
        확인해보세요!
      </p>
    </div>
  </div>
);

const Notices = () => (
  <div className="flex w-full max-w-[375px] flex-col gap-8 px-8">
    <p className="font-700 text-Gray-white typo-caption1">이벤트 주의사항</p>
    <ul className="flex flex-col text-Gray-white/70">
      {LOCAL_EVENT_NOTICES.map((notice, index) => (
        <li key={index} className="ml-16 list-disc font-400 typo-caption1">
          {notice}
        </li>
      ))}
    </ul>
  </div>
);

export const LocalEventSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const shouldReduceMotion = useReducedMotion();

  const { data } = useSuspenseQuery(
    UnauthQueryOptions['get:/wello/v2/allows/home/institution-event'](),
  );

  const events = (data?.context?.contents ?? [])
    .filter((event) => !event.endedYN && !event.title?.includes('_제휴'))
    .sort((a, b) => {
      const aIndex = getOrderIndex(a.institutionName);
      const bIndex = getOrderIndex(b.institutionName);

      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });

  if (events.length === 0) return null;

  if (shouldReduceMotion) {
    return (
      <section className="flex w-full flex-col items-center gap-32 bg-Gray-black px-20 py-60">
        <Header />
        <div className="flex w-full max-w-[375px] flex-col gap-12">
          {events.map((event) => (
            <CustomLink
              key={event.eventId}
              href={DONATION_ROUTES.MY_ETC_EVENT_DETAIL.pathname({
                eventId: event.eventId,
              })}
              className="relative block aspect-[335/120] w-full overflow-hidden rounded-medium"
            >
              <Image
                src={event.bannerImageUrl}
                alt={`${event.institutionName} 이벤트`}
                fill
                sizes="(max-width: 1279px) 100vw, 335px"
                className="object-cover"
              />
            </CustomLink>
          ))}
        </div>
        <Notices />
      </section>
    );
  }

  return (
    <section className="flex w-full flex-col items-center gap-32 bg-Gray-black px-20 py-60">
      <motion.div
        ref={ref}
        variants={staggerContainer}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        className="flex w-full flex-col items-center gap-32"
      >
        <motion.div variants={springBounce}>
          <Header />
        </motion.div>

        <motion.div
          variants={springBounce}
          className="flex w-full max-w-[375px] flex-col gap-12"
        >
          {events.map((event) => (
            <motion.div key={event.eventId} variants={staggerCard}>
              <CustomLink
                href={DONATION_ROUTES.MY_ETC_EVENT_DETAIL.pathname({
                  eventId: event.eventId,
                })}
                className="relative block aspect-[335/120] w-full overflow-hidden rounded-medium"
              >
                <Image
                  src={event.bannerImageUrl}
                  alt={`${event.institutionName} 이벤트`}
                  fill
                  sizes="(max-width: 1279px) 100vw, 335px"
                  className="object-cover"
                />
              </CustomLink>
            </motion.div>
          ))}
        </motion.div>

        <motion.div variants={springBounce}>
          <Notices />
        </motion.div>
      </motion.div>
    </section>
  );
};
